import { TextField } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import type { InputComponent } from '..';

export interface InputTextAreaProps extends InputComponent {
  rows?: number;
}

export function InputTextArea({
  className,
  disabled,
  hidden,
  label,
  hint,
  nameOverride,
  rows = 3,
  required,
  readOnly = false,
  showLabel = !hidden,
  placeholder = '',
}: InputTextAreaProps) {
  const { errors } = useFormikContext<Record<string, string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const inputPlaceholder = useMemo(
    () => placeholder || label?.replace(/[?:]/g, '') || '',
    [placeholder, label],
  );

  return (
    <Field name={name} validate={validate} isInvalid={errors[name]}>
      {({ field }: FieldProps) => (
        <TextField
          multiline
          fullWidth
          variant="outlined"
          rows={rows}
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: {
              shrink: !!(showLabel && label),
            },
            input: {
              inputProps: {
                readOnly,
              } as React.HTMLAttributes<HTMLTextAreaElement>,
            },
          }}
          error={!!errors[name]}
          helperText={errors[name] ? errors[name] : hint}
          {...field}
          value={field.value || ''}
        />
      )}
    </Field>
  );
}
